import {useEffect, useState} from 'react';
import './GameCommandCatalog.css';
import robloxDefinitions from '../../../game-mods/aws/roblox-arriba/commands.json';
import cubosDefinitions from '../../../game-mods/aws/roblox-cubos/commands.json';
import racerDefinitions from '../../../game-mods/aws/roblox-racer/commands.json';
import treemendoDefinitions from '../../../game-mods/aws/roblox-treemendo/commands.json';
import {apiFetch} from '../lib/api';
import s from '../../../renderer/i18n/locales/es.json';
import {catalogEntries, catalogGroups, replaceEntry, addEntry, validateGameCatalog} from '../../../shared/gameCatalog.mjs';

const languages = ['es','en','fr','de','ru','zh','ja','hi','ar','pt','ko','it','id'];
// Roblox games with a bridge catalog: snake_case IDs, and the game announces the IDs it supports.
const robloxDefinitionsByGame = {'roblox-arriba': robloxDefinitions.effects, 'roblox-cubos': cubosDefinitions.effects, 'roblox-racer': racerDefinitions.effects, 'roblox-treemendo': treemendoDefinitions.effects};
const isRoblox = id => Object.hasOwn(robloxDefinitionsByGame, id);
function CatalogImage({src, alt = '', ...props}) {
  const [failed, setFailed] = useState('');
  return src && failed !== src ? <img {...props} src={src} alt={alt} onError={() => setFailed(src)}/> : <span aria-hidden="true">✦</span>;
}
export default function GameCommandCatalog({games = []}) {
  const [search, setSearch] = useState(''), [adding, setAdding] = useState(false);
  const [discovered, setDiscovered] = useState({});
  const [gameId, setGameId] = useState('roblox-arriba');
  const [catalog, setCatalog] = useState(null), [revision, setRevision] = useState(null);
  const [selected, setSelected] = useState(0), [newId, setNewId] = useState(''), [language, setLanguage] = useState('es');
  const [busy, setBusy] = useState(false), [dirty, setDirty] = useState(false), [message, setMessage] = useState('');
  const [draft, setDraft] = useState(null), [invalid, setInvalid] = useState(false);
  const endpoint = `/api/backoffice/gaming/catalogs/${encodeURIComponent(gameId)}`;
  const entries = catalog ? catalogEntries(catalog) : [];
  const entry = entries[selected], effect = entry?.value;
  async function load() {
    setBusy(true); setMessage(''); setCatalog(null); setDraft(null); setInvalid(false);
    try {
      const data = await apiFetch(endpoint);
      catalogEntries(data.catalog); // Fail visibly for an unknown format.
      setCatalog(data.catalog); setDiscovered(data.discovery?.effects || {}); setRevision(data.revision); setSelected(0); setDirty(false); setSearch(''); setAdding(false);
    } catch (error) { setMessage(error.message); }
    finally { setBusy(false); }
  }
  useEffect(() => { load(); }, [gameId]);
  useEffect(() => {
    if(!isRoblox(gameId))return;
    let alive=true, pending=false;
    const timer=setInterval(async()=>{
      if(pending||document.hidden)return;
      pending=true;
      try {const data=await apiFetch(endpoint);if(alive)setDiscovered(data.discovery?.effects||{});}catch{/* Preserve current choices on transient failures. */}
      finally{pending=false;}
    },30000);
    return ()=>{alive=false;clearInterval(timer);};
  },[gameId]);
  const availableDefinitions={...(robloxDefinitionsByGame[gameId] || {}),...discovered};
  function change(value) {
    setCatalog(old => replaceEntry(old, entry.path, {...effect, ...value}));
    setDirty(true); setDraft(null); setInvalid(false);
  }
  function add() {
    const pattern = isRoblox(gameId) ? /^[a-z][a-z0-9_]{0,63}$/ : /^[a-zA-Z0-9][a-zA-Z0-9_.:-]{0,127}$/;
    if (!pattern.test(newId) || ['constructor','prototype','__proto__'].includes(newId)) { setMessage(isRoblox(gameId) ? s.rc_invalid : s.gc_invalid); return; }
    try {
      // Duplicate the selected native definition so protocol-specific fields survive.
      const preset = isRoblox(gameId) && Object.hasOwn(availableDefinitions, newId) ? availableDefinitions[newId] : null;
      const value = structuredClone(preset || effect || {name: {es: newId}, image: ''});
      if ('id' in value) value.id = newId;
      value.enabled = false;
      const group = catalogGroups(catalog).find(group => group.label === entry?.group) || catalogGroups(catalog)[0];
      const updated = addEntry(catalog, group, newId, value);
      setCatalog(updated); setSelected(catalogEntries(updated).findIndex(item => item.id === newId));
      setNewId(''); setDraft(null); setDirty(true); setMessage(''); setSearch(''); setAdding(false);
    } catch (error) { setMessage(error.message); }
  }
  async function upload(event) {
    const file = event.target.files?.[0]; event.target.value = ''; if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setMessage(s.rc_upload); return; }
    setBusy(true); setMessage('');
    try {
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader(); reader.onload = () => resolve(String(reader.result).split(',')[1]);
        reader.onerror = reject; reader.readAsDataURL(file);
      });
      const result = await apiFetch(endpoint + '/image', {method: 'POST', body: {data}});
      change({image: result.url});
    } catch (error) { setMessage(error.message); } finally { setBusy(false); }
  }
  async function publish(event) {
    event.preventDefault(); if (invalid) return;
    if (isRoblox(gameId)) {
      const bad = entries.find(({id}) => !/^[a-z][a-z0-9_]{0,63}$/.test(id));
      if (bad) { setMessage(`${s.rc_invalid} (${bad.id})`); return; }
    }
    if (!validateGameCatalog(catalog)) { setMessage(s.gc_invalid); return; }
    setBusy(true); setMessage('');
    try {
      const data = await apiFetch(endpoint, {method: 'PUT', body: {catalog, revision}});
      setCatalog(data.catalog); setRevision(data.revision); setDirty(false); setMessage(s.agencies_event_published);
    } catch (error) { setMessage(error.message); } finally { setBusy(false); }
  }
  const displayName = item => typeof item.name === 'string' ? item.name : item.name?.es || item.name?.en || '';
  const filtered = entries.map((item, index) => ({...item, index})).filter(item =>
    `${item.id} ${displayName(item.value)}`.toLocaleLowerCase().includes(search.toLocaleLowerCase()));
  return <section className="game-catalog">
    <header className="gc-header">
      <div><span className="gc-eyebrow">{s.gc_events}</span><h2>{s.gc_title}</h2><p>{s.gc_intro}</p></div>
      <div className="gc-game-picker">
        <select aria-label={s.gc_title} disabled={busy || dirty} value={gameId} onChange={event => setGameId(event.target.value)}>
          {games.map(game => <option key={game.id} value={game.id}>{typeof game.name === 'string' ? game.name : game.name?.es || game.id}</option>)}
        </select>
        <button type="button" className="gc-icon-button" disabled={busy || dirty} onClick={load} aria-label={s.rc_reload} title={s.rc_reload}><span aria-hidden="true">↻</span></button>
      </div>
    </header>
    {catalog ? <form onSubmit={publish}>
      <fieldset disabled={busy} className="gc-workspace">
        <aside className="gc-sidebar">
          <div className="gc-list-heading"><strong>{s.gc_events}</strong><span className="gc-count">{entries.length}</span></div>
          <input type="search" aria-label={s.common_search} placeholder={s.common_search} value={search} onChange={event => setSearch(event.target.value)} className="gc-search"/>
          <div className="gc-event-list">
            {filtered.map(item => <button type="button" key={JSON.stringify(item.path)} disabled={invalid} aria-pressed={selected === item.index} onClick={() => {setSelected(item.index); setDraft(null);}} className={`gc-event ${selected === item.index ? 'is-selected' : ''}`}>
              <span className="gc-thumb"><CatalogImage src={item.value.image} loading="lazy"/></span>
              <span className="gc-event-copy"><strong>{displayName(item.value) || item.id}</strong><small>{item.id}</small></span>
              <span className={`gc-status-dot ${item.value.enabled === false ? 'is-off' : ''}`} aria-hidden="true"/>
            </button>)}
            {!filtered.length && <p className="gc-empty">{s.gc_empty}</p>}
          </div>
          <div className="gc-add-area">
            <button type="button" className="gc-add-button" disabled={invalid} aria-expanded={adding} onClick={() => setAdding(!adding)}><span aria-hidden="true">＋</span> {s.common_add}</button>
            {adding && <div className="gc-add-form">
              {isRoblox(gameId) && <select aria-label={s.gc_events} value={Object.hasOwn(availableDefinitions,newId) ? newId : ''} onChange={event => setNewId(event.target.value)}>
                <option value="">{s.common_add}…</option>
                {Object.entries(availableDefinitions).filter(([id]) => !entries.some(entry => entry.id === id)).map(([id, definition]) => <option key={id} value={id}>{definition.name.es} · {id}</option>)}
              </select>}
              <label>{s.rc_id}<input value={newId} onChange={event => setNewId(event.target.value)} onKeyDown={event => {if(event.key === 'Enter'){event.preventDefault();add();}}}/></label>
              <p>{s.gc_duplicate}</p><button type="button" className="gc-primary" disabled={invalid} onClick={add}>{s.common_add}</button>
            </div>}
          </div>
        </aside>
        {effect && <div className="gc-editor">
          <div className="gc-editor-heading"><div><h3>{displayName(effect) || entry.id}</h3><code>{entry.id}</code></div>
            <label className="gc-toggle"><input type="checkbox" disabled={invalid} checked={effect.enabled !== false} onChange={event => change({enabled: event.target.checked})}/><span className="gc-toggle-track" aria-hidden="true"/><span>{s.common_enabled}</span></label>
          </div>
          <fieldset disabled={invalid} className="gc-fields">
            <div className="gc-identity">
              <div className="gc-artwork"><div className="gc-preview"><CatalogImage src={effect.image} alt={displayName(effect) || entry.id}/></div>
                <label className="gc-upload-button"><input type="file" accept="image/png,image/jpeg,image/webp" onChange={upload}/><span>{s.gc_upload_image}</span></label>
                <small>PNG · JPEG · WebP · 2 MB</small>
              </div>
              <div className="gc-identity-fields">
                <label><span className="gc-label-row">{s.rc_name}{typeof effect.name !== 'string' && <select aria-label={s.rc_language} value={language} onChange={event => setLanguage(event.target.value)}>{languages.map(lang => <option key={lang}>{lang}</option>)}</select>}</span>
                  <input value={typeof effect.name === 'string' ? effect.name : effect.name?.[language] || ''} onChange={event => change({name: typeof effect.name === 'string' ? event.target.value : {...effect.name, [language]: event.target.value}})}/>
                </label>
                <details className="gc-image-url"><summary>{s.rc_image}</summary><input aria-label={s.rc_image} type="url" pattern="https://.*" value={effect.image || ''} onChange={event => change({image: event.target.value})}/></details>
              </div>
            </div>
            {['quantity', 'duration'].filter(key => effect[key] && typeof effect[key] === 'object').map(key => <div className="gc-range-card" key={key}>
              <h4>{key === 'quantity' ? s.gc_quantity : s.gc_duration}</h4>
              <div className="gc-range-fields">{[['min',s.rc_min],['max',s.rc_max],['value',s.rc_default]].filter(([field]) => field in effect[key]).map(([field,label]) => <label key={field}>{label}<input type="number" step="any" required value={effect[key][field]} onChange={event => change({[key]: {...effect[key], [field]: event.target.value === '' ? '' : Number(event.target.value)}})}/></label>)}</div>
            </div>)}
          </fieldset>
          <details className="gc-advanced"><summary>{s.gc_advanced}<span aria-hidden="true">⌘</span></summary>
            <p>{s.gc_hint}</p><label>{s.gc_definition}<textarea rows={12} spellCheck={false} value={draft ?? JSON.stringify(effect, null, 2)} onChange={event => {
              const text = event.target.value; setDraft(text); setDirty(true);
              try {
                const value = JSON.parse(text);
                if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error();
                if (typeof entry.path.at(-1) === 'number') {
                  if ('id' in effect) value.id = entry.id; else value.command = entry.id;
                }
                setCatalog(old => replaceEntry(old, entry.path, value)); setInvalid(false);
              } catch { setInvalid(true); }
            }}/></label>
            {invalid && <p role="alert" className="gc-error">{s.gc_invalid}</p>}
          </details>
        </div>}
      </fieldset>
      <footer className="gc-footer"><span className={dirty ? 'gc-unsaved' : 'gc-saved'}><i aria-hidden="true"/>{dirty ? s.gc_pending : s.gc_saved}</span><div>
        {dirty && <button type="button" className="gc-secondary" disabled={busy} onClick={load}>{s.common_cancel}</button>}
        <button type="submit" disabled={busy || !dirty || invalid} className="gc-primary">{busy ? s.common_loading : s.agencies_publish}<span aria-hidden="true"> ↗</span></button>
      </div></footer>
    </form> : busy && <div className="gc-empty">{s.common_loading}…</div>}
    {message && <p className="gc-message" role="status">{message}</p>}
  </section>;
}
